import React, { useContext, useEffect, useState } from "react";
import { TURBINE_DATA_TIMEOUT_MS } from "../../constants";
import styled, { ThemeContext } from "styled-components";
import moment from "moment";
import { ScreenSizes } from "components/shared";
import RPM from "components/dashboard/RPM";
import Pitch from "components/dashboard/Pitch";
import LiveDataGraph from "containers/live/LiveDataGraph";
import CciDsoStatus, { isBoardDataFresh } from "components/live/CciDsoStatus";
import Temperature from "containers/weather/Temperature";
import { Grid } from "semantic-ui-react";
import { FaWind, FaExclamationTriangle } from "react-icons/fa";
import { GiElectric } from "react-icons/gi";
import { Progress, SignalBar } from "components/shared";
import { WindTurbineMode } from "components/shared";
import { Text } from "components/shared";

const ErrorOverlay = styled.div`
  position: absolute;
  border-radius: 3px;
  opacity: 0.8;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding: 1rem;
  background-color: ${(props) =>
    props.displayerror ? "#404B4A" : "transparent"};
  height: 5rem;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  margin: auto;

  ${ScreenSizes.greaterThan("mobile")`
  display: ${(props) =>
    props.displayerror && props.fulldetails ? "none" : "flex"} 
  `}
`;

const Error = styled((props) => <Text {...props} />)`
  color: red;
  font-weight: 800;
  z-index: 1;
`;

const GridColumn = styled((props) => <Grid.Column {...props} />)`
  &&& {
    opacity: ${(props) => (props.stale ? 0.5 : 1)};
    filter: ${(props) => (props.stale ? "grayscale(40%)" : "none")};
    transition: opacity 0.3s ease, filter 0.3s ease;
    ${ScreenSizes.lessThan("mobile")`
  padding: 0rem !important;
  `}
  }
`;

const GeneralGrid = styled((props) => <Grid {...props} />)`
  &&& {
    ${ScreenSizes.lessThan("mobile")`
  `}
  }
`;

// Mirrors SignalWrapper on the left: the A/M mode badge used to sit at the
// end of the RPM/Pitch/Temp row, which wrapped onto a second line on mobile
// once the values got wider (2026-10-05).
const ModeWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 1rem;
  z-index: 1;
`;

const SignalWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 1rem;
  opacity: ${(props) => (props.connected ? 1 : 0.2)};
  transition: opacity 0.3s ease;
`;

const TimeoutBanner = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.35rem 0.75rem;
  border-radius: 0 0 8px 8px;
  background-color: ${(props) => `${props.theme.warnColor}D9`};
`;

const TimeoutLabel = styled((props) => <Text {...props} />)`
  font-weight: 800;
  color: #fff;
`;

const TimeoutTime = styled((props) => <Text {...props} />)`
  color: #fff;
  opacity: 0.85;
`;

const GaugeRow = styled((props) => <Grid.Row {...props} />)`
  position: relative;
`;

const DATA_DELAY = TURBINE_DATA_TIMEOUT_MS;

const DashboardCardContent = ({
  // Defaulted, not just destructured - see CardHeader.js's identical guard
  // for why: windTurbine can be momentarily undefined during navigation
  // between two turbines' detail pages, and this component reads
  // windTurbine.maxPower/latitude/longitude further down with no null
  // check, which crashed the whole page (no error boundary) exactly the
  // same way CardHeader's unguarded windTurbine.name did.
  windTurbine = {},
  fullDetails,
  liveProps,
  turbineState,
}) => {
  const { rpm_rotor, pitch_1, wind, power, mode, err } = liveProps;
  const themeContext = useContext(ThemeContext);
  const [displayError, updateDisplayError] = useState(false);

  // getSignal()/connected below only get re-evaluated when this component
  // re-renders, which otherwise only happens when new turbineState/liveProps
  // props arrive - if the turbine goes silent (Modbus/Ethernet/MQTT loss),
  // nothing would ever trigger a re-render again and the gauges/Timeout
  // banner would stay frozen showing the last-known "connected" state
  // forever. This tick forces a re-render every couple seconds purely so the
  // elapsed-time check gets re-run.
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((t) => t + 1), 2000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!!err && err !== 0) {
      updateDisplayError(true);
    } else {
      updateDisplayError(false);
    }
  }, [err]);

  const renderGraph = (height) => (
    <LiveDataGraph windTurbine={windTurbine} height={height} />
  );

  const getSignal = (turbineState) => {
    // An explicit online:false from the bridge's MQTT status message (clean
    // MQTT disconnect via LWT, or a PLC/Modbus failure - see mqttIngest.js)
    // means "actually offline right now" - don't wait out the rest of the
    // DATA_DELAY grace window in that case.
    if (turbineState && (turbineState.online === false || turbineState.plcOnline === false)) return 0;
    return turbineState && turbineState.lastUpdate
      ? (turbineState.lastUpdate - Date.now() + DATA_DELAY) / 1000
      : 0;
  };

  const renderRotationTemperatureAndMode = () => {
    return (
      <Grid.Row
        style={{
          alignItems: "center",
          justifyContent: "space-around",
          gap: "0.5rem",
          padding: "0rem",
          marginTop: "1.5rem",
        }}
      >
        <RPM value={rpm_rotor} />
        <Pitch value={pitch_1} />
        <Temperature
          coordinates={{
            latitude: windTurbine.latitude,
            longitude: windTurbine.longitude,
          }}
        />
      </Grid.Row>
    );
  };

  const renderCircularProgressMeter = ({
    type,
    value,
    maxValue,
    strokeStats,
    icon,
    unit,
    tickPercent,
    caption,
  }) => {
    return (
      <Progress
        type={type}
        strokeColor={strokeStats}
        maxValue={maxValue}
        value={value}
        strokeWidth={9}
        icon={icon}
        unit={unit}
        trailColor={themeContext.circleBackground}
        fulldetails={fullDetails}
        err={err}
        gapDegree={0}
        gapPosition="left"
        tickPercent={tickPercent}
        tickColor={themeContext.textMediumLight}
        caption={caption}
      />
    );
  };

  // Full 360° ring, origin at the left (9 o'clock), sweeping clockwise. The
  // power gauge is scaled to 3x the turbine's rated power (admin setting)
  // so the rated value always lands exactly at the 120° tick.
  const ratedPower = windTurbine.maxPower || 60;

  const firstProgressMeter = {
    type: "circle",
    value: power,
    maxValue: ratedPower * 3,
    strokeStats: themeContext.primaryColor,
    icon: <GiElectric color={themeContext.primaryColor} />,
    unit: "kW",
    tickPercent: 100 / 3,
    caption: `/ ${ratedPower} rated`,
  };

  const secondProgressMeter = {
    type: "circle",
    value: wind,
    maxValue: 60,
    strokeStats: themeContext.secondaryColor,
    icon: <FaWind color={themeContext.secondaryColor} />,
    unit: "m/s",
  };

  const renderVisualMeters = () => {
    const connected = getSignal(turbineState) > 0;
    // Bridge reachable over MQTT but its PLC isn't answering Modbus - the
    // board is at the turbine and online, just no data to show.
    const plcDown =
      !!turbineState &&
      turbineState.online === true &&
      turbineState.plcOnline === false;
    return (
      <Grid padded="horizontally" columns={2}>
        <GaugeRow
          verticalAlign="middle"
          style={{ maxWidth: "35rem", margin: "auto" }}
        >
          <ModeWrapper>
            <WindTurbineMode mode={mode === 0 ? "M" : "A"} />
          </ModeWrapper>
          <SignalWrapper connected={connected}>
            <SignalBar signal={4} />
          </SignalWrapper>
          <GridColumn stale={!connected}>
            {renderCircularProgressMeter(firstProgressMeter)}
          </GridColumn>
          <GridColumn stale={!connected}>
            {renderCircularProgressMeter(secondProgressMeter)}
          </GridColumn>
          {!connected && (
            <TimeoutBanner>
              <FaExclamationTriangle size={14} color="#fff" />
              <TimeoutLabel size={13}>
                {plcDown ? "Online · Modbus offline" : "Timeout"}
              </TimeoutLabel>
              <TimeoutTime size={11}>
                {turbineState && turbineState.lastUpdate
                  ? moment(turbineState.lastUpdate).fromNow()
                  : "N/A"}
              </TimeoutTime>
            </TimeoutBanner>
          )}
          {connected && (
            <ErrorOverlay
              fulldetails={fullDetails}
              displayerror={displayError}
              justifyContent={fullDetails ? "flex-start" : "center"}
              paddingLeft={fullDetails ? 200 : 16}
            >
              <Error size={20}>{err}</Error>
            </ErrorOverlay>
          )}
        </GaugeRow>
        {renderRotationTemperatureAndMode()}
      </Grid>
    );
  };

  return (
    <GeneralGrid
      stackable
      fulldetails={fullDetails ? 1 : 0}
      columns={fullDetails ? 2 : 1}
    >
      {renderVisualMeters()}
      {fullDetails && (
        <Grid.Column verticalAlign="middle">
          {/* On the free line right under the LIVE/HISTORY/ADVANCED/SERVICE
              LOGS menu, above the graph (2026-10-05 request) - not as an
              extra row of its own under RPM/Pitch/Temp. */}
          <CciDsoStatus
            liveProps={liveProps}
            connected={isBoardDataFresh(turbineState)}
          />
          {fullDetails && renderGraph()}
        </Grid.Column>
      )}
    </GeneralGrid>
  );
};

DashboardCardContent.propTypes = {};

DashboardCardContent.defaultProps = {};

export default React.memo(DashboardCardContent);
